import React from 'react';
import { styled } from '@mui/material/styles';
import { APICONSTANTS, CONSTANTS } from '../Constants';
import { Grid } from '@mui/material';
import PedigreeBox from './PedigreeBox';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'src/axios';
import { EVENTS } from 'src/components/admin/appReducer';
import { usePedigreeLock } from 'src/hooks/usePedigreeLock';
import { usePersonLocks } from 'src/hooks/usePersonLocks';
import { useQueryClient } from '@tanstack/react-query';
import { usePedigreeInstance } from './pedigree-tool/pedigree/instanceContext';
import { useWorkspace } from 'src/store/WorkspaceContext';
import {
  shouldFetchFamily,
  familyLoadFailureEffects
} from './workspace/familyLoadPolicy';
import { BACKGROUND_LOCK_IDLE_RELEASE_MS } from 'src/store/workspace/workspaceConfig';

const Container = styled(Grid)({
  position: 'absolute',
  height: '100%',
  // The v7 Grid `container` no longer stretches to full width by default
  // (unlike the legacy Grid), so set it explicitly — the pedigree canvas reads
  // its parent's clientWidth/clientHeight and renders blank at zero width.
  width: '100%',
  top: 0,
  left: 0,
  overflow: 'hidden !important'
});

function PedigreeChartView({
  patient,
  familyId: familyIdProp,
  previewFamilyId,
  onPreviewError
}) {
  const isPreview = !!previewFamilyId;
  const { content } = React.useContext(AppContext);
  const {
    pedigreeSelectedUsers: { selectedUser }
  } = content;

  const navigate = useNavigate();
  // In the multi-pedigree-tabs workspace the host mounts several instances at
  // once and passes each its own familyId; a lone chart (flag off) still reads
  // it from the route param. A read-only FHQ preview is never route-driven —
  // it names its shadow family explicitly, so it wins over both.
  const { _id: routeFamilyId } = useParams();
  const familyId = isPreview ? previewFamilyId : familyIdProp ?? routeFamilyId;
  const { dispatchUpdate } = React.useContext(AppContext);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // In the multi-pedigree-tabs workspace, opening is VIEW-ONLY: the lock is not
  // taken on open (so N open tabs hold 0 locks) — it is acquired lazily on the
  // first edit of the active tab. A lone chart (no instance provider) keeps the
  // classic acquire-on-open behaviour.
  const { instanceId, isActive } = usePedigreeInstance();
  const ws = useWorkspace();
  // `!isPreview` is load-bearing, not defensive: React context flows through the
  // MUI Dialog portal, so an FhqPreviewModal opened from inside the pedigree
  // subtree would inherit a non-null instanceId and start driving the HOST tab's
  // lock badge and tooltip with the SHADOW family's state. Gating here makes
  // every workspace side effect below inert in preview at a single point.
  const workspaceMode = instanceId != null && !isPreview;
  // Use the STABLE workspace callbacks (each useCallback([]) in the provider), not
  // the `ws` object — `ws` gets a new identity on every workspace-state change, so
  // depending on it in an effect that also dispatches is an infinite loop.
  const wsSetLock = ws?.setLock;
  const wsMarkEdited = ws?.markEdited;
  const wsSetProbandName = ws?.setProbandName;
  const wsMarkInaccessible = ws?.markInaccessible;

  const {
    lockState,
    isMine,
    requestStatus,
    requestLock,
    cancelRequestLock,
    forceAcquireLock,
    pendingRequest,
    respondToRequest,
    acquire,
    releaseLock
  } = usePedigreeLock(isPreview ? null : familyId, {
    // `!isPreview` as well as `!workspaceMode`: a preview is NOT workspace mode,
    // so autoAcquire would otherwise be true and a read-only dialog would take a
    // real edit lock on the shadow family and write an audit event saying so.
    autoAcquire: !workspaceMode && !isPreview,
    active: !workspaceMode || isActive
  });

  // In preview the chart is read-only, and that is enforced through isLocked —
  // it gates onSumbit, hides Bulk edit, and disables undo/redo, lasso and the
  // node menus. effLockState stays null so LockBanner early-returns instead of
  // claiming the family is "locked by another user". Do not collapse effIsMine /
  // effLockState back to isMine / lockState.
  const isLocked = isPreview ? true : !!(lockState && !isMine);
  const effIsMine = isPreview ? false : isMine;
  const effLockState = isPreview ? null : lockState;
  const { setSnack } = React.useContext(SnackContext);

  // Mark self-initiated releases (idle-release + manual release) so the lock-lost
  // watcher below doesn't mistake them for another user taking the lock.
  const selfReleaseRef = React.useRef(false);
  const doRelease = React.useCallback(() => {
    selfReleaseRef.current = true;
    releaseLock?.();
  }, [releaseLock]);

  // First edit of the active workspace tab: acquire the lock + auto-pin the tab so
  // the working context can't be silently recycled. Idempotent.
  const onEdit = React.useCallback(() => {
    if (!workspaceMode || !isActive) return;
    acquire();
    if (instanceId) wsMarkEdited?.(instanceId);
  }, [workspaceMode, isActive, acquire, instanceId, wsMarkEdited]);

  // Keep the tab's lock badge in sync with reality. Depends on the stable
  // setLock callback (not `ws`) + the actual lock inputs, so it only fires when
  // the badge value could change — SET_LOCK is also a no-op when unchanged.
  React.useEffect(() => {
    if (!workspaceMode || !instanceId || !wsSetLock) return;
    wsSetLock(instanceId, isMine ? 'self' : isLocked ? 'other' : 'none');
  }, [workspaceMode, instanceId, wsSetLock, isMine, isLocked]);

  // Populate the tab tooltip with the proband name once the family has loaded, so
  // users can identify a family without knowing its G-number. SET_PROBAND_NAME is
  // a no-op when unchanged.
  const probandName =
    [selectedUser?.firstName, selectedUser?.lastName]
      .filter(Boolean)
      .join(' ') ||
    selectedUser?.name ||
    '';
  React.useEffect(() => {
    if (!workspaceMode || !familyId || !wsSetProbandName || !probandName)
      return;
    wsSetProbandName(familyId, probandName);
  }, [workspaceMode, familyId, wsSetProbandName, probandName]);

  // Background-tab lock release: if this tab holds the edit lock but has been the
  // INACTIVE tab for the idle window, release it so it never hogs the lock while a
  // clinician works elsewhere. The pedigree autosaves continuously, so by the idle
  // window everything is persisted. Re-activating (isActive) or losing the lock
  // (isMine) cancels the timer via the effect cleanup.
  React.useEffect(() => {
    if (!workspaceMode || !isMine || isActive) return;
    const timer = setTimeout(doRelease, BACKGROUND_LOCK_IDLE_RELEASE_MS);
    return () => clearTimeout(timer);
  }, [workspaceMode, isMine, isActive, doRelease]);

  // Manual "Release lock" from the tab menu: the workspace bumps this session's
  // releaseNonce; release the lock if we hold it (works for a background tab).
  const releaseNonce =
    (workspaceMode &&
      ws?.state.sessions.find((s) => s.familyId === familyId)?.releaseNonce) ||
    0;
  const prevReleaseNonceRef = React.useRef(releaseNonce);
  React.useEffect(() => {
    if (releaseNonce !== prevReleaseNonceRef.current) {
      prevReleaseNonceRef.current = releaseNonce;
      if (isMine) doRelease();
    }
  }, [releaseNonce, isMine, doRelease]);

  // Lock-lost: if we held the edit lock and it went away WITHOUT our releasing it
  // (another user force-took it, or it expired), tell the user cleanly — the chart
  // is now view-only and their unsaved work is protected by the autosave.
  const prevIsMineRef = React.useRef(isMine);
  React.useEffect(() => {
    const was = prevIsMineRef.current;
    prevIsMineRef.current = isMine;
    if (was && !isMine) {
      if (!selfReleaseRef.current && workspaceMode) {
        setSnack({
          status: true,
          severity: 'warning',
          msg: 'Your edit lock on this pedigree was taken by another user — it is now view-only.'
        });
      }
      selfReleaseRef.current = false;
    }
  }, [isMine, workspaceMode, setSnack]);

  const personLockHook = usePersonLocks(isPreview ? null : familyId, {
    active: !workspaceMode || isActive
  });

  // A session whose family could not be loaded (deleted, merged, or belonging to
  // another tenant) is flagged in the workspace. It must not be retried: the retry
  // is what turned one bad tab into a lockout, because every visit to the pedigree
  // route re-fetched it and the failure handler moved the router (TGCLOUD-1618).
  const sessionInaccessible =
    (workspaceMode &&
      ws?.state.sessions.find((s) => s.familyId === familyId)?.inaccessible) ||
    false;

  // Load ON ACTIVATION, not on mount — see shouldFetchFamily. Latched here (state,
  // not a ref, because the fetch effect has to re-run when it flips) so switching
  // away and back does not refetch.
  const [hasBeenActive, setHasBeenActive] = React.useState(
    !workspaceMode || isActive
  );
  React.useEffect(() => {
    if (isActive) setHasBeenActive(true);
  }, [isActive]);

  const getPedigreeDetails = () => {
    if (!familyId) return;
    // NOTE: deliberately does NOT drive the global LoadingContext / black BackdropLoader.
    // The Canvas renders its own "Loading family…" overlay while the worker computes the
    // layout; showing the global black spinner too produced a double spinner on open (the
    // backdrop's fade-out overlapped the Canvas overlay). This view's loading UX is the
    // Canvas overlay alone.
    axios
      // ?auditView=1 marks this as a genuine record OPEN (a Tier-2 access event),
      // distinct from the post-edit data reloads that hit the same endpoint. A
      // preview is not a record open, so it is omitted there.
      .get(
        `${APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)}${
          isPreview ? '' : '?auditView=1'
        }`
      )
      .then((resp) => {
        const { proband, families, perf } = resp.data;

        const value = {
          family: families,
          selectedUser: proband,
          perf
        };

        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });
      })
      .catch(() => {
        // The rules live in familyLoadPolicy so they are pinned by tests rather
        // than only reachable by mounting this whole view. The tab strip already
        // renders "(unavailable)" off `inaccessible` and offers a close button, so
        // flagging is all this needs to do to explain itself.
        const effects = familyLoadFailureEffects({
          isPreview,
          workspaceMode,
          isActive
        });
        if (effects.notifyPreview) onPreviewError?.();
        if (effects.markInaccessible) wsMarkInaccessible?.(familyId);
        if (effects.navigateToList) navigate(CONSTANTS.pedigreeToolPath);
      });
  };

  React.useEffect(() => {
    if (!shouldFetchFamily({ familyId, sessionInaccessible, hasBeenActive })) {
      return;
    }
    getPedigreeDetails();
  }, [familyId, sessionInaccessible, hasBeenActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const pedigree = React.useMemo(() => {
    return (
      <>
        {selectedUser?._id && (
          <PedigreeBox
            patient={patient}
            isLocked={isLocked}
            isMine={effIsMine}
            lockState={effLockState}
            requestStatus={isPreview ? null : requestStatus}
            onRequestLock={isPreview ? () => {} : requestLock}
            onCancelRequest={isPreview ? () => {} : cancelRequestLock}
            onForceAcquireLock={isPreview ? () => {} : forceAcquireLock}
            pendingRequest={isPreview ? null : pendingRequest}
            onRespondToRequest={isPreview ? () => {} : respondToRequest}
            personLockHook={personLockHook}
            onEdit={isPreview ? undefined : onEdit}
            preview={isPreview}
          />
        )}
      </>
    );
  }, [
    patient,
    selectedUser,
    isLocked,
    effIsMine,
    effLockState,
    requestStatus,
    pendingRequest,
    personLockHook,
    onEdit,
    isPreview
  ]);

  return <Container container>{pedigree}</Container>;
}

export default PedigreeChartView;
